import React, { useEffect, useRef, useState } from "react";

const steps = [
  {
    id: "match",
    number: "01",
    label: "Match",
    headline: "Upload your CV. Match with real jobs.",
    description:
      "JobZam reads your CV and job descriptions to show how strongly you match before you spend time applying.",
    accent: "MATCH 92%",
  },
  {
    id: "tailor",
    number: "02",
    label: "Tailor",
    headline: "Turn generic bullets into role-ready achievements.",
    description:
      "Improve your resume using the actual job description, with ATS-friendly changes shown clearly before download.",
    accent: "ATS READY",
  },
  {
    id: "apply",
    number: "03",
    label: "Apply",
    headline: "Resume and cover letter ready in minutes.",
    description:
      "Generate tailored application materials and download them in PDF or DOCX format when you are ready.",
    accent: "READY TO SEND",
  },
  {
    id: "interview",
    number: "04",
    label: "Interview",
    headline: "Prepare for interviews based on the role.",
    description:
      "Get role-specific questions, answer guidance, and confidence-building prep based on your CV and the job description.",
    accent: "INTERVIEW READY",
  },
  {
    id: "track",
    number: "05",
    label: "Track",
    headline: "Save jobs, prepare interviews, and improve faster.",
    description:
      "Keep your job search organised with saved jobs, interview prep, and human CV review support when needed.",
    accent: "NEXT STEP",
  },
];

function MatchCard() {
  return (
    <div className="overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-500 p-5 text-white shadow-2xl lg:p-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-blue-100">
        01 · CV Match
      </p>

      <h3 className="max-w-xl text-2xl font-semibold leading-tight md:text-3xl">
        92% match for Senior Data Engineer
      </h3>

      <div className="mt-6 rounded-3xl border border-white/20 bg-white/15 p-4 backdrop-blur">
        <div className="flex items-center justify-between border-b border-white/20 pb-4">
          <div>
            <p className="text-sm text-blue-100">Target role</p>
            <p className="mt-1 font-semibold">Senior Data Engineer · London</p>
          </div>
          <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-blue-700">
            92%
          </span>
        </div>

        <div className="mt-5 space-y-3">
          {["Python", "AWS", "PySpark", "ETL", "Stakeholder communication"].map(
            (item) => (
              <div key={item} className="flex items-center justify-between gap-4">
                <span className="text-sm text-white/90 md:text-base">{item}</span>
                <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white md:text-sm">
                  matched
                </span>
              </div>
            )
          )}
        </div>
      </div>

      <p className="mt-5 text-sm font-medium text-blue-50">
        → JobZam finds the strongest matching roles first
      </p>
    </div>
  );
}

function TailorCard() {
  return (
    <div className="rounded-[1.5rem] border border-blue-100 bg-white p-5 shadow-xl lg:p-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
        02 · Resume Tailoring
      </p>

      <h3 className="max-w-xl text-2xl font-semibold leading-tight text-slate-900 md:text-3xl">
        See exactly what changed before you download.
      </h3>

      <div className="mt-6 rounded-3xl bg-blue-50/70 p-4">
        <p className="mb-3 text-sm font-semibold text-gray-500">
          Experience bullet improved
        </p>

        <div className="rounded-2xl border border-red-100 bg-white p-4 text-sm text-gray-500 line-through md:text-base">
          Worked on data pipelines and reports for business teams.
        </div>

        <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-gray-900 md:text-base">
          Built Python and AWS data pipelines, reducing manual reporting time by
          38% across weekly business workflows.
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {["Python", "AWS", "impact", "metrics", "ATS keywords"].map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ApplyCard() {
  return (
    <div className="overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-indigo-700 via-blue-700 to-blue-500 p-5 text-white shadow-2xl lg:p-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-blue-100">
        03 · Application Pack
      </p>

      <h3 className="max-w-xl text-2xl font-semibold leading-tight md:text-3xl">
        Resume, cover letter, and job details in one flow.
      </h3>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-white/20 bg-white/15 p-4 backdrop-blur">
          <p className="text-sm text-blue-100">Resume</p>
          <p className="mt-2 text-base font-semibold md:text-lg">
            Tailored_Resume.pdf
          </p>
          <button className="mt-5 rounded-full bg-white px-4 py-2 text-sm font-bold text-blue-700">
            Download PDF
          </button>
        </div>

        <div className="rounded-3xl border border-white/20 bg-white/15 p-4 backdrop-blur">
          <p className="text-sm text-blue-100">Cover letter</p>
          <p className="mt-2 text-base font-semibold md:text-lg">
            Built from the JD
          </p>
          <button className="mt-5 rounded-full bg-cyan-300 px-4 py-2 text-sm font-bold text-blue-950">
            Download DOCX
          </button>
        </div>
      </div>

      <p className="mt-5 text-sm font-medium text-blue-50">
        → Built from your CV and the real job description
      </p>
    </div>
  );
}

function InterviewPrepCard() {
  return (
    <div className="rounded-[1.5rem] border border-blue-100 bg-white p-5 shadow-xl lg:p-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
        04 · Interview Prep
      </p>

      <h3 className="max-w-xl text-2xl font-semibold leading-tight text-slate-900 md:text-3xl">
        Practice questions built from the job description.
      </h3>

      <div className="mt-6 rounded-3xl bg-blue-50/70 p-4">
        <div className="rounded-2xl border border-blue-100 bg-white p-4">
          <p className="text-sm font-semibold text-gray-500">Question 1</p>
          <p className="mt-2 text-sm text-slate-900 md:text-base">
            Tell me about a time you improved a data pipeline or automated a
            manual process.
          </p>
        </div>

        <div className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
          <p className="text-sm font-semibold text-gray-500">
            Suggested answer angle
          </p>
          <p className="mt-2 text-sm text-gray-700 md:text-base">
            Use your Python, AWS, and reporting experience. Explain the problem,
            the action you took, and the measurable result.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {["STAR method", "role-specific", "confidence", "sample answers"].map(
            (tag) => (
              <span
                key={tag}
                className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700"
              >
                {tag}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function TrackCard() {
  return (
    <div className="rounded-[1.5rem] border border-blue-100 bg-white p-5 shadow-xl lg:p-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
        05 · Track & Improve
      </p>

      <h3 className="max-w-xl text-2xl font-semibold leading-tight text-slate-900 md:text-3xl">
        Keep your job search moving, not scattered.
      </h3>

      <div className="mt-6 space-y-3 rounded-3xl bg-blue-50/70 p-4">
        {[
          ["Saved jobs", "12 roles"],
          ["Applied jobs", "5 applications"],
          ["Interview prep", "3 ready"],
          ["Human CV review", "Available"],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between gap-4 rounded-2xl border border-blue-100 bg-white p-4"
          >
            <span className="text-sm font-medium text-gray-600 md:text-base">
              {label}
            </span>
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700 md:text-sm">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const cards = {
  match: <MatchCard />,
  tailor: <TailorCard />,
  apply: <ApplyCard />,
  interview: <InterviewPrepCard />,
  track: <TrackCard />,
};

const STICKY_TOP_OFFSET = 96;
const gradientTextClass =
  "bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent";

export default function StickyWorkflowShowcase() {
  const wrapperRef = useRef(null);
  const [activeStep, setActiveStep] = useState("match");

  useEffect(() => {
    const updateActiveStep = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const wrapperTop = window.scrollY + rect.top;
      const scrollStart = wrapperTop - STICKY_TOP_OFFSET;
      const scrollableDistance =
        wrapper.offsetHeight - window.innerHeight + STICKY_TOP_OFFSET;

      if (scrollableDistance <= 0) return;

      const scrolled = Math.min(
        Math.max(window.scrollY - scrollStart, 0),
        scrollableDistance
      );
      const progress = scrolled / scrollableDistance;

      const index = Math.min(
        steps.length - 1,
        Math.floor(progress * steps.length)
      );

      setActiveStep(steps[index].id);
    };

    updateActiveStep();

    window.addEventListener("scroll", updateActiveStep, { passive: true });
    window.addEventListener("resize", updateActiveStep);

    return () => {
      window.removeEventListener("scroll", updateActiveStep);
      window.removeEventListener("resize", updateActiveStep);
    };
  }, []);

  const activeIndex = steps.findIndex((step) => step.id === activeStep);
  const active = steps[activeIndex] || steps[0];

  const goToStep = (index) => {
    setActiveStep(steps[index].id);

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const wrapperTop = window.scrollY + wrapper.getBoundingClientRect().top;
    const scrollStart = wrapperTop - STICKY_TOP_OFFSET;
    const scrollableDistance =
      wrapper.offsetHeight - window.innerHeight + STICKY_TOP_OFFSET;

    const target = scrollStart + scrollableDistance * (index / steps.length);

    window.scrollTo({
      top: target,
      behavior: "smooth",
    });
  };

  return (
    <section className="-mx-6 mt-10 bg-gradient-to-b from-slate-50 via-blue-50/70 to-slate-50 px-6 py-14 md:-mx-16 md:px-16 lg:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-3xl">
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-blue-600">
            How JobZam works
          </p>

          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
            From CV match{" "}
            <span className={gradientTextClass}>to confident application.</span>
          </h2>

          <p className="mt-5 text-base leading-7 text-gray-600 md:text-xl md:leading-8">
            A modern product demo that shows how JobZam helps job seekers match
            roles, tailor resumes, generate documents, and prepare for
            interviews.
          </p>
        </div>

        {/* Desktop sticky scroll version */}
        <div ref={wrapperRef} className="relative hidden h-[320vh] lg:block">
          <div className="sticky top-24 grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <div className="mb-8 flex flex-wrap gap-3">
                {steps.map((step, index) => {
                  const isActive = step.id === activeStep;

                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => goToStep(index)}
                      className={`rounded-full border px-5 py-3 text-sm font-bold transition-all ${
                        isActive
                          ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-200"
                          : "border-gray-200 bg-white text-gray-500 hover:border-blue-300 hover:text-blue-700"
                      }`}
                    >
                      <span className="mr-2 opacity-70">{step.number}</span>
                      {step.label}
                    </button>
                  );
                })}
              </div>

              <div className="rounded-[1.5rem] border border-blue-100 bg-white/90 p-5 shadow-xl shadow-blue-100/60 backdrop-blur lg:p-6">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
                  {active.number} · {active.accent}
                </p>

                <h3 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                  {active.headline}
                </h3>

                <p className="mt-4 text-base leading-7 text-gray-600">
                  {active.description}
                </p>
              </div>
            </div>

            <div className="transition-all duration-500 ease-out">
              <div key={activeStep} className="animate-[fadeIn_0.35s_ease-out]">
                {cards[activeStep]}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile normal stacked version */}
        <div className="space-y-8 lg:hidden">
          {steps.map((step) => (
            <div key={step.id}>
              <div className="mb-3 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
                  {step.number} · {step.accent}
                </p>

                <h3 className="mt-2 text-xl font-semibold text-slate-900">
                  {step.headline}
                </h3>

                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {step.description}
                </p>
              </div>

              {cards[step.id]}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(14px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </section>
  );
}
